import React, { useEffect, useState } from "react";
import {
  Box,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  Paper,
  CircularProgress,
  Tooltip,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { useSiteDataApi } from "../utilities/SiteDataProvider";

/**
 * Styled MUI components
 */
const StyledTableCell = styled(TableCell)(({ theme }) => ({
  backgroundColor: theme.palette.grey[900],
  color: theme.palette.common.white,
  fontWeight: "bold",
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: theme.palette.action.hover,
  },
}));

/**
 * Mapping of models/public insurers → category + revenue value
 */
const CATEGORY_MAP = {
  Elite: {
    keywords: [
      "Vivia 9",
      "Nexia 9",
      "Edge AI 24",
      "Genesis AI 24",
      "Intent 1",
      "7IX",
      "Starkey Signature Series 24",
    ],
    value: 3750,
  },
  Premium: {
    keywords: [
      "Vivia 7",
      "Nexia 7",
      "Edge AI 20",
      "Genesis AI 20",
      "Intent 2",
      "5IX",
      "Starkey Signature Series 20",
    ],
    value: 3000,
  },
  Select: {
    keywords: [
      "Vivia 5",
      "Nexia 5",
      "Edge AI 16",
      "Genesis AI 16",
      "Intent 3",
      "3IX",
      "Starkey Signature Series 16",
    ],
    value: 2250,
  },
  Custom: {
    keywords: ["Savi 3"],
    value: 1500,
  },
  WSIB: { value: 800 },
  VAC: { value: 1800 },
  "First Canadian Health (NIHB)": { value: 1325 },
  ODSP: { value: 1800 },
};

/**
 * Determine category based on model keywords or public insurer list
 */
const getCategory = (model, insurer) => {
  const insurers = (insurer || "").split(",").map((s) => s.trim());

  // Public‑insurer categories override model matching
  for (const name of ["WSIB", "VAC", "First Canadian Health (NIHB)", "ODSP"]) {
    if (insurers.includes(name)) return name;
  }

  for (const [cat, { keywords }] of Object.entries(CATEGORY_MAP)) {
    if (keywords && keywords.some((k) => model.includes(k))) return cat;
  }

  return "Uncategorized";
};

const TreatmentStartSummary = () => {
  const [categorySummary, setCategorySummary] = useState({});
  const [locationSummary, setLocationSummary] = useState({});
  const [loading, setLoading] = useState(true);
  const [siteDataContext] = useSiteDataApi();

  /** Fetch & aggregate data on mount */
  useEffect(() => {
    const fetchTreatmentData = async () => {
      try {
        const res = await fetch("/api/upcoming-ts", {
          headers: {
            Accept: "application/json",
            "Content-Type": "application/json",
            "X-CSRF-Token": siteDataContext.thhcsfrtoken,
          },
        });
        const data = await res.json();
        const entries = data.returnData[0];

        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);
        const startOfMonth = new Date(today.getFullYear(), today.getMonth(), 1);
        const endOfMonth = new Date(
          today.getFullYear(),
          today.getMonth() + 1,
          0,
          23,
          59,
          59
        );

        const catMap = {};
        const locMap = {};

        entries.forEach((entry) => {
          const model = entry.model_name || "Unknown";
          const status = Number(entry.status);
          const category = getCategory(model, entry.public_insurers);
          const startDate = new Date(entry.start_time);
          startDate.setSeconds(0, 0);
          const location = entry.location_name || "Unknown";
          const catVal = CATEGORY_MAP[category]?.value || 0;

          const dateOnly = (d) =>
            new Date(d.getFullYear(), d.getMonth(), d.getDate());

          const isStarted =
            status === 7 &&
            dateOnly(startDate) >= dateOnly(startOfMonth) &&
            dateOnly(startDate) <= dateOnly(yesterday);

          const isRemaining =
            status !== 7 &&
            dateOnly(startDate) >= dateOnly(today) &&
            dateOnly(startDate) <= dateOnly(endOfMonth);

          // Init buckets
          if (!catMap[category]) {
            catMap[category] = {
              thisMonth: 0,
              remaining: 0,
              valueSoFar: 0,
              projectedValue: 0,
            };
          }
          if (!locMap[location]) {
            locMap[location] = { valueSoFar: 0, restOfMonth: 0, monthTotal: 0 };
          }

          // Category aggregation
          if (isStarted) {
            catMap[category].thisMonth += 1;
            catMap[category].valueSoFar += catVal;
            catMap[category].projectedValue += catVal;
          }
          if (isRemaining) {
            catMap[category].remaining += 1;
            catMap[category].projectedValue += catVal;
          }

          // Location aggregation
          if (isStarted) {
            locMap[location].valueSoFar += catVal;
            locMap[location].monthTotal += catVal;
          }
          if (isRemaining) {
            locMap[location].restOfMonth += catVal;
            locMap[location].monthTotal += catVal;
          }
        });

        setCategorySummary(catMap);
        setLocationSummary(locMap);
      } catch (err) {
        console.error("Failed to fetch treatment data", err);
      } finally {
        setLoading(false);
      }
    };

    fetchTreatmentData();
  }, []);

  if (loading) {
    return (
      <Box mt={4} textAlign="center">
        <CircularProgress />
        <Typography mt={2}>Loading treatment summary...</Typography>
      </Box>
    );
  }

  /** Prepare rows & totals */
  const catRows = Object.entries(categorySummary);
  const catTotals = catRows.reduce(
    (acc, [, v]) => {
      acc.thisMonth += v.thisMonth;
      acc.remaining += v.remaining;
      acc.valueSoFar += v.valueSoFar;
      acc.projectedValue += v.projectedValue;
      return acc;
    },
    { thisMonth: 0, remaining: 0, valueSoFar: 0, projectedValue: 0 }
  );

  const locRows = Object.entries(locationSummary);
  const locTotals = locRows.reduce(
    (acc, [, v]) => {
      acc.valueSoFar += v.valueSoFar;
      acc.restOfMonth += v.restOfMonth;
      acc.monthTotal += v.monthTotal;
      return acc;
    },
    { valueSoFar: 0, restOfMonth: 0, monthTotal: 0 }
  );

  /** Render tables */
  return (
    <Box m={3}>
      <Typography variant="h5" gutterBottom>
        Treatment Start Summary (June 1–30, 2025)
      </Typography>

      {/* -------- CATEGORY TABLE -------- */}
      <TableContainer component={Paper} sx={{ mb: 4 }}>
        <Table size="small">
          <TableHead>
            <StyledTableRow>
              <StyledTableCell>Category</StyledTableCell>
              <StyledTableCell align="center">
                Started This Month
              </StyledTableCell>
              <StyledTableCell align="center">Value So Far</StyledTableCell>
              <StyledTableCell align="center">Remaining Starts</StyledTableCell>
              <StyledTableCell align="center">Projected Value</StyledTableCell>
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {catRows.map(([cat, v]) => (
              <StyledTableRow key={cat}>
                <TableCell>{cat}</TableCell>
                <TableCell align="center">{v.thisMonth}</TableCell>
                <TableCell align="center">
                  ${v.valueSoFar.toLocaleString()}
                </TableCell>
                <TableCell align="center">{v.remaining}</TableCell>
                <TableCell align="center">
                  ${v.projectedValue.toLocaleString()}
                </TableCell>
              </StyledTableRow>
            ))}
            <StyledTableRow>
              <StyledTableCell>Total</StyledTableCell>
              <StyledTableCell align="center">
                {catTotals.thisMonth}
              </StyledTableCell>
              <StyledTableCell align="center">
                ${catTotals.valueSoFar.toLocaleString()}
              </StyledTableCell>
              <StyledTableCell align="center">
                {catTotals.remaining}
              </StyledTableCell>
              <StyledTableCell align="center">
                ${catTotals.projectedValue.toLocaleString()}
              </StyledTableCell>
            </StyledTableRow>
          </TableBody>
        </Table>
      </TableContainer>

      {/* -------- LOCATION TABLE -------- */}
      <Typography variant="h6" gutterBottom>
        Revenue by Location (June 1–30, 2025)
      </Typography>
      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <StyledTableRow>
              <StyledTableCell>Location</StyledTableCell>
              <StyledTableCell align="center">Value So Far</StyledTableCell>
              <StyledTableCell align="center">Rest of Month</StyledTableCell>
              <StyledTableCell align="center">Month Total</StyledTableCell>
            </StyledTableRow>
          </TableHead>
          <TableBody>
            {locRows.map(([loc, v]) => (
              <StyledTableRow key={loc}>
                <TableCell>{loc}</TableCell>
                <TableCell align="center">
                  ${v.valueSoFar.toLocaleString()}
                </TableCell>
                <TableCell align="center">
                  ${v.restOfMonth.toLocaleString()}
                </TableCell>
                <TableCell align="center">
                  ${v.monthTotal.toLocaleString()}
                </TableCell>
              </StyledTableRow>
            ))}
            <StyledTableRow>
              <StyledTableCell>Total</StyledTableCell>
              <StyledTableCell align="center">
                ${locTotals.valueSoFar.toLocaleString()}
              </StyledTableCell>
              <StyledTableCell align="center">
                ${locTotals.restOfMonth.toLocaleString()}
              </StyledTableCell>
              <StyledTableCell align="center">
                ${locTotals.monthTotal.toLocaleString()}
              </StyledTableCell>
            </StyledTableRow>
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default TreatmentStartSummary;
